// Comparison Data for Color Palette Comparison System
// This data drives programmatic SEO pages

export type CharacteristicDetail = {
  title: string;
  description: string;
};

export type SeasonData = {
  id: string; // "light-spring"
  name: string; // "Light Spring"
  parent: "spring" | "summer" | "autumn" | "winter";
  temperature: "warm" | "cool" | "neutral";
  value: "light" | "medium" | "deep";
  chroma: "soft" | "bright" | "muted";
  description: string;
  characteristics: string[];
  characteristicDetails: CharacteristicDetail[];
  colors: { name: string; hex: string }[];
  celebrities: string[];
  bestFor: string[];
  avoid: string[];
};

// All 12 subseasons
export const seasons: SeasonData[] = [
  // SPRING
  {
    id: "light-spring",
    name: "Light Spring",
    parent: "spring",
    temperature: "warm",
    value: "light",
    chroma: "bright",
    description: "Light Spring is the lightest and most delicate of the warm seasons. With warm undertones and light, clear coloring, Light Springs have a fresh, youthful appearance that glows in soft, warm pastels.",
    characteristics: ["Light coloring", "Warm undertones", "Low to medium contrast", "Golden or peachy warmth"],
    characteristicDetails: [
      { title: "Warm Undertones", description: "Your coloring carries a subtle golden or peachy warmth—never pink or blue-based. This warmth is gentle rather than intense, giving you a sun-kissed quality even without a tan." },
      { title: "Light Value", description: "There's a luminous, airy quality to your overall appearance. Your best colors share this lightness—think morning sunlight rather than midday intensity." },
      { title: "Delicate Contrast", description: "The difference between your hair, skin, and eyes is soft rather than dramatic. Heavy, dark colors can overwhelm your natural harmony." },
    ],
    colors: [
      { name: "Peach", hex: "#FFDAB3" },
      { name: "Apricot", hex: "#FBCEB1" },
      { name: "Light Coral", hex: "#F88379" },
      { name: "Buttercream", hex: "#FFF1C1" },
      { name: "Mint Green", hex: "#C4E1C1" },
      { name: "Light Aqua", hex: "#B3E3E2" },
      { name: "Blush Pink", hex: "#FADADD" },
      { name: "Daffodil", hex: "#FFFF99" },
    ],
    celebrities: ["Taylor Swift", "Blake Lively", "Amanda Seyfried", "Scarlett Johansson"],
    bestFor: ["Warm undertones", "Light eyes (blue, green, light hazel)", "Blonde or light brown hair", "Fair to light skin with golden undertones"],
    avoid: ["Black", "Dark navy", "Charcoal", "Burgundy", "Deep jewel tones"],
  },
  {
    id: "true-spring",
    name: "True Spring",
    parent: "spring",
    temperature: "warm",
    value: "medium",
    chroma: "bright",
    description: "True Spring is the quintessential warm season—vibrant, clear, and golden. True Springs have medium contrast with distinctly warm coloring that comes alive in saturated, clear warm hues.",
    characteristics: ["Medium contrast", "Distinctly warm undertones", "Clear, bright coloring", "Golden warmth throughout"],
    characteristicDetails: [
      { title: "Distinctly Warm", description: "Warmth is the defining quality of your coloring. Your skin, eyes, and hair all carry golden or peachy notes—cool colors simply don't resonate with your natural palette." },
      { title: "Clear & Bright", description: "There's nothing muted about True Spring. Your colors need clarity and vibrancy—dusty or grayed tones will make you look tired rather than radiant." },
      { title: "Medium Contrast", description: "You have enough contrast to wear bold colors, but not so much that you need the extremes of black and white. Your sweet spot is rich, saturated warmth." },
    ],
    colors: [
      { name: "Coral", hex: "#FF6F61" },
      { name: "Golden Yellow", hex: "#FFD700" },
      { name: "Turquoise", hex: "#40E0D0" },
      { name: "Warm Poppy", hex: "#FF6347" },
      { name: "Jade", hex: "#00A86B" },
      { name: "Amber", hex: "#FFBF00" },
      { name: "Orange", hex: "#FFA500" },
      { name: "Azure", hex: "#007FFF" },
    ],
    celebrities: ["Amy Adams", "Emma Stone", "Nicole Kidman", "Jessica Chastain"],
    bestFor: ["Warm undertones", "Green, hazel, or warm brown eyes", "Red, auburn, or warm brown hair", "Warm, peachy, or golden skin"],
    avoid: ["Muted colors", "Dusty shades", "Cool grays", "Black"],
  },
  {
    id: "bright-spring",
    name: "Bright Spring",
    parent: "spring",
    temperature: "warm",
    value: "medium",
    chroma: "bright",
    description: "Bright Spring sits between Spring and Winter, combining warmth with high contrast. Bright Springs have clear, vivid coloring that can handle the most saturated, electric colors.",
    characteristics: ["High contrast", "Warm with some coolness", "Clear, vivid coloring", "Can handle very bright colors"],
    characteristicDetails: [
      { title: "High Contrast", description: "Your features create a striking visual impact—there's drama in the difference between your dark hair and bright eyes, or your vivid coloring against your skin." },
      { title: "Warm-Leaning Neutral", description: "While primarily warm, you can borrow from Winter's clarity. This versatility means you can wear both warm brights and clear cool tones." },
      { title: "Electric Saturation", description: "Muted colors disappear on you. You need the voltage turned up—think neon coral, electric turquoise, vivid yellow." },
    ],
    colors: [
      { name: "Neon Coral", hex: "#FF5E5B" },
      { name: "Electric Yellow", hex: "#FFFF00" },
      { name: "Bright Aqua", hex: "#00FFFF" },
      { name: "Hot Tomato", hex: "#FF6347" },
      { name: "Bright Lime", hex: "#A7FC00" },
      { name: "Turquoise Blue", hex: "#00CED1" },
      { name: "Warm Pink", hex: "#FF69B4" },
      { name: "Clear Orange", hex: "#FF9900" },
    ],
    celebrities: ["Mila Kunis", "Robert Downey Jr.", "Lea Michele", "Zendaya"],
    bestFor: ["High contrast coloring", "Clear, bright eyes", "Dark hair with warm undertones", "Can handle both warm and bright cool colors"],
    avoid: ["Muted colors", "Dusty shades", "Dull olive", "Muddy browns"],
  },

  // SUMMER
  {
    id: "light-summer",
    name: "Light Summer",
    parent: "summer",
    temperature: "cool",
    value: "light",
    chroma: "soft",
    description: "Light Summer is the softest and most delicate of the cool seasons. With cool undertones and light, muted coloring, Light Summers have an ethereal quality that suits soft, dusty pastels.",
    characteristics: ["Light coloring", "Cool undertones", "Low contrast", "Soft, muted appearance"],
    characteristicDetails: [
      { title: "Cool Undertones", description: "There's a subtle rose or blue quality to your coloring—a coolness that makes warm, golden tones feel discordant against your skin." },
      { title: "Ethereal Lightness", description: "Your coloring has a soft-focus, almost dreamlike quality. Heavy or saturated colors overwhelm this delicate balance." },
      { title: "Gentle Contrast", description: "Your features blend harmoniously rather than contrasting sharply. Your palette should echo this softness—no harsh lines or bold statements." },
    ],
    colors: [
      { name: "Powder Pink", hex: "#FFD1DC" },
      { name: "Misty Blue", hex: "#B4CFEC" },
      { name: "Seafoam", hex: "#B5EAD7" },
      { name: "Lilac", hex: "#C8A2C8" },
      { name: "Soft Lavender", hex: "#E6E6FA" },
      { name: "Powder Blue", hex: "#B0E0E6" },
      { name: "Rose Quartz", hex: "#F7CAC9" },
      { name: "Mint", hex: "#98FF98" },
    ],
    celebrities: ["Elle Fanning", "Naomi Watts", "Cate Blanchett", "Gwyneth Paltrow"],
    bestFor: ["Cool undertones", "Light blue, gray, or soft green eyes", "Ash blonde or light brown hair", "Fair, pink-toned skin"],
    avoid: ["Black", "Bright orange", "Warm camel", "High contrast colors"],
  },
  {
    id: "true-summer",
    name: "True Summer",
    parent: "summer",
    temperature: "cool",
    value: "medium",
    chroma: "soft",
    description: "True Summer is the classic cool, muted season. True Summers have medium contrast with distinctly cool undertones, looking best in soft, grayed colors that don't overpower their delicate coloring.",
    characteristics: ["Medium contrast", "Cool undertones", "Muted, soft coloring", "Rose or blue undertones in skin"],
    characteristicDetails: [
      { title: "Purely Cool", description: "Your undertones are distinctly blue-based or rose-toned. Warm colors create visual discord, while cool tones bring instant harmony to your complexion." },
      { title: "Softly Muted", description: "Bright, saturated colors compete with rather than complement your coloring. Your best shades have a gentle, grayed quality—think watercolor rather than acrylic." },
      { title: "Medium Depth", description: "You sit comfortably in the middle range—neither the lightest nor the deepest. This gives you versatility within your muted, cool palette." },
    ],
    colors: [
      { name: "Dusty Rose", hex: "#D4A5A5" },
      { name: "Slate Blue", hex: "#5D6D8E" },
      { name: "Mauve", hex: "#E0B0FF" },
      { name: "Sage", hex: "#BCB88A" },
      { name: "Smoky Blue", hex: "#6B7B8C" },
      { name: "Dusty Lavender", hex: "#B39EB5" },
      { name: "Rosewood", hex: "#B38481" },
      { name: "Moss Green", hex: "#8A9A5B" },
    ],
    celebrities: ["Jennifer Aniston", "Sarah Jessica Parker", "Emily Blunt", "Reese Witherspoon"],
    bestFor: ["Cool undertones", "Soft eye colors (gray, blue-gray, soft green)", "Ash brown or mousy hair", "Skin with pink or rose undertones"],
    avoid: ["Bright warm colors", "Black", "Orange", "Warm mustard"],
  },
  {
    id: "soft-summer",
    name: "Soft Summer",
    parent: "summer",
    temperature: "cool",
    value: "medium",
    chroma: "muted",
    description: "Soft Summer sits between Summer and Autumn, the most muted of all seasons. Soft Summers have low contrast with gently cool coloring, suiting the softest, most grayed colors.",
    characteristics: ["Low contrast", "Cool-neutral undertones", "Very muted coloring", "Soft, blended appearance"],
    characteristicDetails: [
      { title: "Cool-Neutral", description: "Your undertones lean cool but aren't purely blue-based. This neutral quality gives you some flexibility, but warmth should always be subtle." },
      { title: "The Most Muted", description: "Of all twelve seasons, you have the softest coloring. Bright colors look garish on you; your palette is hushed, grayed, and sophisticated." },
      { title: "Blended Features", description: "There's little contrast between your hair, skin, and eyes—they flow together. Your colors should mirror this seamless quality." },
    ],
    colors: [
      { name: "Medium Navy", hex: "#3B5998" },
      { name: "Teal", hex: "#3E8E8E" },
      { name: "Plum", hex: "#8E4585" },
      { name: "Steel Gray", hex: "#71797E" },
      { name: "Muted Rose", hex: "#C76B8A" },
      { name: "Ocean Blue", hex: "#4F84C4" },
      { name: "Raspberry", hex: "#9B3A62" },
      { name: "Cool Spruce", hex: "#4A7C6F" },
    ],
    celebrities: ["Jessica Alba", "Jennifer Lopez", "Leighton Meester", "Dakota Johnson"],
    bestFor: ["Neutral-cool undertones", "Soft, muted eye colors", "Medium brown hair with ash tones", "Skin that's neither very warm nor very cool"],
    avoid: ["Bright colors", "Warm orange", "High contrast combinations", "Pure white"],
  },

  // AUTUMN
  {
    id: "soft-autumn",
    name: "Soft Autumn",
    parent: "autumn",
    temperature: "warm",
    value: "medium",
    chroma: "muted",
    description: "Soft Autumn sits between Autumn and Summer, the warmest of the muted seasons. Soft Autumns have low contrast with warm, earthy coloring that suits soft, dusty warm tones.",
    characteristics: ["Low contrast", "Warm-neutral undertones", "Muted, soft coloring", "Earthy, natural appearance"],
    characteristicDetails: [
      { title: "Warm-Neutral", description: "Your undertones carry warmth, but it's subdued rather than golden. You share territory with Summer, which is why overly bright warm colors can feel too intense." },
      { title: "Earthen Softness", description: "Think of autumn haze—your colors have that same dusty, organic quality. Bright or artificial-looking shades feel out of place." },
      { title: "Low Contrast", description: "Your features blend gently together. High-contrast combinations—like black and white—create visual tension that doesn't suit your natural harmony." },
    ],
    colors: [
      { name: "Muted Gold", hex: "#D4AF37" },
      { name: "Dusty Coral", hex: "#D8A48F" },
      { name: "Sage", hex: "#A3AA8B" },
      { name: "Soft Rust", hex: "#C87C5A" },
      { name: "Olive", hex: "#808000" },
      { name: "Warm Taupe", hex: "#D2B1A3" },
      { name: "Terracotta", hex: "#E2725B" },
      { name: "Mocha", hex: "#967969" },
    ],
    celebrities: ["Drew Barrymore", "Gisele Bundchen", "Jennifer Aniston", "Sarah Jessica Parker"],
    bestFor: ["Warm-neutral undertones", "Soft hazel, green, or warm brown eyes", "Medium brown hair with golden highlights", "Skin with golden or olive undertones"],
    avoid: ["Black", "Pure white", "Bright fuchsia", "Cool navy"],
  },
  {
    id: "true-autumn",
    name: "True Autumn",
    parent: "autumn",
    temperature: "warm",
    value: "medium",
    chroma: "muted",
    description: "True Autumn is the quintessential warm, rich season. True Autumns have medium contrast with distinctly warm, golden coloring that comes alive in saturated earth tones.",
    characteristics: ["Medium contrast", "Distinctly warm undertones", "Rich, golden coloring", "Natural warmth throughout"],
    characteristicDetails: [
      { title: "Unmistakably Warm", description: "Golden undertones run through every aspect of your coloring. Cool colors look foreign on you; warm earth tones feel like coming home." },
      { title: "Rich & Saturated", description: "Unlike the softer Autumns, you can handle depth and richness. Your colors are the full-bodied reds of turning leaves, the burnt orange of harvest." },
      { title: "Medium Contrast", description: "You have definition without drama. This balance means you look best in colors with similar intensity—not washed out, not overpowering." },
    ],
    colors: [
      { name: "Goldenrod", hex: "#DAA520" },
      { name: "Pumpkin", hex: "#FF7518" },
      { name: "Copper", hex: "#B87333" },
      { name: "Rust", hex: "#B7410E" },
      { name: "Warm Saffron", hex: "#F4C430" },
      { name: "Bronze", hex: "#CD7F32" },
      { name: "Warm Olive", hex: "#6B8E23" },
      { name: "Sienna", hex: "#A0522D" },
    ],
    celebrities: ["Julia Roberts", "Marcia Cross", "Julianne Moore", "Christina Hendricks"],
    bestFor: ["Warm undertones", "Green, hazel, or warm brown eyes", "Red, auburn, or warm brown hair", "Golden or olive skin"],
    avoid: ["Pure white", "Black", "Cool pink", "Cool navy"],
  },
  {
    id: "deep-autumn",
    name: "Deep Autumn",
    parent: "autumn",
    temperature: "warm",
    value: "deep",
    chroma: "muted",
    description: "Deep Autumn sits between Autumn and Winter, combining warmth with depth. Deep Autumns have high contrast with rich, warm coloring that suits deep, saturated earth tones.",
    characteristics: ["High contrast", "Warm undertones with depth", "Rich, intense coloring", "Can handle dark, saturated colors"],
    characteristicDetails: [
      { title: "Warm with Depth", description: "You carry Autumn's warmth but with added intensity. Where other Autumns stay in the mid-tones, you can go darker—deep olive, rich mahogany, dark amber." },
      { title: "High Contrast", description: "The difference between your features is striking. This drama means you can wear bold, deep colors that would overwhelm lighter seasons." },
      { title: "Jewel-Toned Richness", description: "Think of your palette as Autumn after sunset—the same warm hues but deeper, more mysterious. You share territory with Deep Winter but stay on the warm side." },
    ],
    colors: [
      { name: "Deep Mahogany", hex: "#4A1C17" },
      { name: "Deep Olive", hex: "#556B2F" },
      { name: "Burnt Umber", hex: "#8A3324" },
      { name: "Deep Rust", hex: "#8B2500" },
      { name: "Forest Green", hex: "#228B22" },
      { name: "Maroon", hex: "#800000" },
      { name: "Walnut", hex: "#773F1A" },
      { name: "Dark Amber", hex: "#996515" },
    ],
    celebrities: ["Eva Mendes", "Penelope Cruz", "Salma Hayek", "Sofia Vergara"],
    bestFor: ["Warm undertones with depth", "Dark brown or warm black eyes", "Dark brown or black hair with warm highlights", "Deep golden or olive skin"],
    avoid: ["Icy pastels", "Pure white", "Cool blue", "Cool fuchsia"],
  },

  // WINTER
  {
    id: "cool-winter",
    name: "Cool Winter",
    parent: "winter",
    temperature: "cool",
    value: "medium",
    chroma: "bright",
    description: "Cool Winter (also called Clear Winter) is the most purely cool of all seasons. Cool Winters have high contrast with distinctly cool coloring that suits icy, clear colors.",
    characteristics: ["High contrast", "Distinctly cool undertones", "Clear, icy coloring", "Can handle very bright cool colors"],
    characteristicDetails: [
      { title: "Purely Cool", description: "Of all seasons, your undertones are the most distinctly blue-based. Any hint of warmth creates immediate discord—you need colors as cool as a winter sky." },
      { title: "Icy Clarity", description: "Your colors should have crystalline clarity—nothing muddy, nothing dusty. Think of light through an icicle: pure, bright, and sharp." },
      { title: "High Contrast", description: "There's natural drama in your coloring. The difference between your dark hair, bright eyes, and skin creates an impact that demands equally bold colors." },
    ],
    colors: [
      { name: "Ice Blue", hex: "#A5F2F3" },
      { name: "Magenta", hex: "#FF00FF" },
      { name: "Sapphire", hex: "#0F52BA" },
      { name: "Berry", hex: "#990066" },
      { name: "Burgundy", hex: "#800020" },
      { name: "Violet", hex: "#8A2BE2" },
      { name: "Azure", hex: "#007FFF" },
      { name: "Rose", hex: "#C21E56" },
    ],
    celebrities: ["Anne Hathaway", "Courtney Cox", "Brooke Shields", "Demi Moore"],
    bestFor: ["Cool undertones", "Blue, gray, or cool green eyes", "Dark brown or black hair", "Cool pink or olive skin"],
    avoid: ["Warm beige", "Camel", "Golden brown", "Terracotta"],
  },
  {
    id: "true-winter",
    name: "True Winter",
    parent: "winter",
    temperature: "cool",
    value: "deep",
    chroma: "bright",
    description: "True Winter is the quintessential high-contrast season. True Winters have dramatic contrast between dark hair, light skin, and bright eyes, suiting the most vivid, saturated colors.",
    characteristics: ["Very high contrast", "Cool undertones", "Clear, vivid coloring", "Dramatic appearance"],
    characteristicDetails: [
      { title: "Dramatic Contrast", description: "Snow White coloring—the fairest skin against the darkest hair. This extreme contrast is your defining feature, and your colors should match this intensity." },
      { title: "Cool & Clear", description: "Your undertones are decidedly cool, and your colors must have absolute clarity. Any muddiness or warmth diminishes your natural brilliance." },
      { title: "Maximum Saturation", description: "You can wear colors at their most intense—pure red, electric blue, stark black and white. What would overwhelm other seasons makes you come alive." },
    ],
    colors: [
      { name: "Electric Blue", hex: "#0055FF" },
      { name: "Hot Pink", hex: "#FF69B4" },
      { name: "Ruby", hex: "#E0115F" },
      { name: "Black", hex: "#000000" },
      { name: "Bright Purple", hex: "#A020F0" },
      { name: "Cyan", hex: "#00FFFF" },
      { name: "Fuchsia", hex: "#D200D2" },
      { name: "Pure White", hex: "#FFFFFF" },
    ],
    celebrities: ["Megan Fox", "Liv Tyler", "Lucy Liu", "Katy Perry"],
    bestFor: ["Cool undertones", "Bright blue, green, or dark brown eyes", "Black or very dark brown hair", "Porcelain or cool olive skin"],
    avoid: ["Warm cream", "Camel", "Rust orange", "Warm gold"],
  },
  {
    id: "deep-winter",
    name: "Deep Winter",
    parent: "winter",
    temperature: "cool",
    value: "deep",
    chroma: "muted",
    description: "Deep Winter sits between Winter and Autumn, combining coolness with depth. Deep Winters have high contrast with rich, cool coloring that suits deep, jewel-toned colors.",
    characteristics: ["High contrast", "Cool undertones with depth", "Rich, intense coloring", "Can handle very dark colors"],
    characteristicDetails: [
      { title: "Cool with Depth", description: "Your coolness has weight to it—not icy, but deep. Think of a midnight sky versus a winter morning. Your colors carry the same mysterious intensity." },
      { title: "High Contrast", description: "The interplay between your dark hair, striking eyes, and skin creates natural drama. You can anchor bold, deep colors that would overwhelm lighter seasons." },
      { title: "Jewel-Tone Territory", description: "Deep ruby, sapphire, emerald—your palette lives in the realm of precious stones. You share depth with Deep Autumn but stay firmly on the cool side." },
    ],
    colors: [
      { name: "Deep Navy", hex: "#000080" },
      { name: "Deep Ruby", hex: "#9B111E" },
      { name: "Deep Emerald", hex: "#005F5F" },
      { name: "Aubergine", hex: "#580F41" },
      { name: "Wine", hex: "#722F37" },
      { name: "Deep Sapphire", hex: "#082567" },
      { name: "Deep Magenta", hex: "#8B008B" },
      { name: "Midnight Green", hex: "#004953" },
    ],
    celebrities: ["Kim Kardashian", "Sandra Bullock", "Catherine Zeta-Jones", "Priyanka Chopra"],
    bestFor: ["Cool undertones with depth", "Dark brown or black eyes", "Black or very dark hair", "Deep cool skin (any depth)"],
    avoid: ["Warm ivory", "Camel", "Terracotta", "Warm moss"],
  },
];

// Generate all valid comparisons (canonical only - alphabetical order)
export function generateComparisons(): { slug: string; seasonA: SeasonData; seasonB: SeasonData }[] {
  const comparisons: { slug: string; seasonA: SeasonData; seasonB: SeasonData }[] = [];

  for (let i = 0; i < seasons.length; i++) {
    for (let j = i + 1; j < seasons.length; j++) {
      const seasonA = seasons[i];
      const seasonB = seasons[j];

      // Alphabetical ordering for canonical URLs
      const [first, second] = [seasonA, seasonB].sort((a, b) => a.id.localeCompare(b.id));

      comparisons.push({
        slug: `${first.id}-vs-${second.id}`,
        seasonA: first,
        seasonB: second,
      });
    }
  }

  return comparisons;
}

// Get comparison by slug
export function getComparisonBySlug(slug: string): { seasonA: SeasonData; seasonB: SeasonData } | null {
  const parts = slug.split("-vs-");
  if (parts.length !== 2) return null;

  const [idA, idB] = parts;
  const seasonA = seasons.find(s => s.id === idA);
  const seasonB = seasons.find(s => s.id === idB);

  if (!seasonA || !seasonB) return null;

  return { seasonA, seasonB };
}

// Get all slugs for static generation
export function getAllComparisonSlugs(): string[] {
  return generateComparisons().map(c => c.slug);
}

// Generate difference text based on season characteristics
export function generateDifferenceText(seasonA: SeasonData, seasonB: SeasonData): string {
  const tempA = seasonA.temperature === "warm" ? "warm" : "cool";
  const tempB = seasonB.temperature === "warm" ? "warm" : "cool";

  const chromaA = seasonA.chroma;
  const chromaB = seasonB.chroma;

  const valueA = seasonA.value;
  const valueB = seasonB.value;

  let differences: string[] = [];

  // Temperature difference
  if (tempA !== tempB) {
    differences.push(`${seasonA.name} is ${tempA}-toned while ${seasonB.name} is ${tempB}-toned`);
  } else {
    differences.push(`Both seasons share ${tempA} undertones`);
  }

  // Chroma difference
  if (chromaA !== chromaB) {
    differences.push(`${seasonA.name} is ${chromaA} while ${seasonB.name} is ${chromaB}`);
  }

  // Value difference
  if (valueA !== valueB) {
    differences.push(`${seasonA.name} works with ${valueA} colors whereas ${seasonB.name} suits ${valueB} colors`);
  }

  return differences.join(". ") + ".";
}

// Group comparisons by parent season for the hub page
export function getComparisonsByParent(): Record<string, { slug: string; name: string }[]> {
  const allComparisons = generateComparisons();
  const grouped: Record<string, { slug: string; name: string }[]> = {
    spring: [],
    summer: [],
    autumn: [],
    winter: [],
  };

  for (const comparison of allComparisons) {
    const displayName = `${comparison.seasonA.name} vs ${comparison.seasonB.name}`;

    // Add to the parent season of seasonA
    if (!grouped[comparison.seasonA.parent].find(c => c.slug === comparison.slug)) {
      grouped[comparison.seasonA.parent].push({ slug: comparison.slug, name: displayName });
    }
  }

  return grouped;
}

// Get related comparisons for internal linking
export function getRelatedComparisons(currentSlug: string, limit: number = 6): { slug: string; name: string }[] {
  const current = getComparisonBySlug(currentSlug);
  if (!current) return [];

  const allComparisons = generateComparisons();
  const related: { slug: string; name: string }[] = [];

  for (const comparison of allComparisons) {
    if (comparison.slug === currentSlug) continue;

    // Include if either season matches
    if (comparison.seasonA.id === current.seasonA.id ||
        comparison.seasonA.id === current.seasonB.id ||
        comparison.seasonB.id === current.seasonA.id ||
        comparison.seasonB.id === current.seasonB.id) {
      related.push({
        slug: comparison.slug,
        name: `${comparison.seasonA.name} vs ${comparison.seasonB.name}`,
      });
    }

    if (related.length >= limit) break;
  }

  return related;
}
